import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="font-body container mx-auto px-4 py-8">
        <header className="mb-8 border-b border-gray-200 dark:border-gray-700 pb-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Chameleon Eye, LLC
            </p>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-2 mb-2">Privacy Policy</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated: March 19, 2026</p>
        </header>
        <main className="max-w-3xl text-gray-800 dark:text-gray-200">
            <p className="leading-relaxed mb-6">
            Chameleon Eye, LLC ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share information about you when you use our services, including our website, applications, and related features (collectively, the "Service"). By using the Service, you agree to the terms of this Privacy Policy. If you do not agree with this policy, please do not use the Service.
            </p>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 1</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">What Information Do We Collect?</h2>
            <p>We collect the following types of information:</p>

            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">a. Information You Provide to Us</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and a hashed version of your password.</li>
                <li><strong>Course Responses:</strong> As part of your interaction with our course questions, you may voluntarily submit information, which may sometimes include personal information.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">b. Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Usage Data:</strong> We collect data about your interactions with the Service, such as IP addresses, browser type, and timestamps.</li>
                <li><strong>Cookies and Tracking Technologies:</strong> We use cookies to improve your experience and gather usage statistics. For more information, refer to our Cookie Policy.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">c. Information Processed for Feedback Responses</h3>
            <p>To provide personalized feedback, we use APIs to send your course responses and relevant user data to GPT-based tools. This data is used solely for generating tailored feedback and improving your learning experience.</p>
            </section>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 2</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">How Do We Use Your Information?</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2">
                <li>Provide, personalize, and improve the Service.</li>
                <li>Generate feedback responses using AI models.</li>
                <li>Communicate with you, including sending updates and notifications about your account or the Service.</li>
                <li>Analyze trends and improve our course offerings.</li>
                <li>Ensure security and prevent fraud.</li>
            </ul>
            </section>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 3</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">How Do We Share Your Information?</h2>
            <p>We do not sell or rent your personal information. We share your information only as follows:</p>

            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">a. Service Providers</h3>
            <p>We work with third-party service providers to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Process data (e.g., API-based tools for generating feedback responses).</li>
                <li>Provide hosting and infrastructure services.</li>
            </ul>
            <p>These providers are authorized to use your information solely to provide the necessary services on our behalf.</p>

            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">b. Legal Compliance</h3>
            <p>We may share information if required to comply with legal obligations, enforce our terms of service, or protect the rights, property, or safety of Chameleon Eye, LLC, our users, or others.</p>

            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">c. Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction. We will notify you of any such changes.</p>
            </section>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 4</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">How Do We Protect Your Information?</h2>
            <p>We implement technical and organizational measures to safeguard your information, including:</p>
            <ul className="list-disc pl-6 space-y-2">
                <li>Hashing passwords to ensure secure storage.</li>
                <li>Encrypting data in transit where applicable.</li>
                <li>Regularly updating security measures to address potential vulnerabilities.</li>
            </ul>
            </section>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 5</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">Your Rights</h2>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2 my-3">
                <li><strong>Access:</strong> Request a copy of the data we hold about you.</li>
                <li><strong>Correction:</strong> Request corrections to any inaccurate information.</li>
                <li><strong>Deletion:</strong> Request the deletion of your data, subject to legal and contractual obligations.</li>
                <li><strong>Restriction:</strong> Request that we restrict processing of your information in certain circumstances.</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a portable format.</li>
            </ul>
            <p>To exercise these rights, contact us at <a href="mailto:support@makerstate.com">support@makerstate.com</a>.</p>
            </section>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 6</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">Children&apos;s Privacy</h2>
            <p>The Service is not intended for individuals under the age of 13. If we learn that we have collected information from a child under 13 without parental consent, we will delete such information promptly. Please contact us at <a href="mailto:support@makerstate.com">support@makerstate.com</a> if you believe we have inadvertently collected such information.</p>
            </section>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 7</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of the Service after such changes indicates your acceptance of the updated policy.</p>
            </section>

            <section className="mb-10">
            <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Section 8</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy, please reach out:</p>
            <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4 my-2">
                <p>
                  Email:{' '}
                  <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@makerstate.com">
                    support@makerstate.com
                  </a>
                  <br />
                  <span className="text-sm opacity-70">Owned and operated by Chameleon Eye, LLC</span>
                </p>
            </div>
            </section>

        </main>
    </div>
  );
};

export default PrivacyPolicy;